import type { ReactNode } from "react";
import { useInView } from "@/hooks/useInView";

type Direction = "up" | "down" | "left" | "right" | "none";

type RevealProps = {
  children: ReactNode;
  /** Slide direction the content travels from. */
  from?: Direction;
  /** Delay in ms before the reveal animation starts. */
  delay?: number;
  /** Override the default 700ms duration. */
  durationMs?: number;
  /** Intersection threshold (0–1). */
  threshold?: number;
  /** Additional classes (applied to the wrapper). */
  className?: string;
  /** Use an <li> wrapper instead of a <div> for list items. */
  as?: "div" | "li" | "span";
};

/**
 * Lightweight scroll-reveal wrapper. Fades + slides children in once they
 * enter the viewport. Respects prefers-reduced-motion (the hook makes
 * everything visible immediately in that case).
 */
export default function Reveal({
  children,
  from = "up",
  delay = 0,
  durationMs = 700,
  threshold = 0.15,
  className = "",
  as = "div",
}: RevealProps) {
  const { ref, inView } = useInView<HTMLElement>({ threshold });

  const hidden =
    from === "up"
      ? "opacity-0 translate-y-6"
      : from === "down"
        ? "opacity-0 -translate-y-6"
        : from === "left"
          ? "opacity-0 -translate-x-6"
          : from === "right"
            ? "opacity-0 translate-x-6"
            : "opacity-0";

  const style = {
    transitionDuration: `${durationMs}ms`,
    transitionDelay: `${delay}ms`,
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    transitionProperty: "opacity, transform",
    willChange: "opacity, transform",
  } as const;

  const cls = `${className} ${inView ? "opacity-100 translate-x-0 translate-y-0" : hidden}`.trim();

  if (as === "li") {
    return (
      <li ref={ref as React.RefObject<HTMLLIElement>} className={cls} style={style}>
        {children}
      </li>
    );
  }
  if (as === "span") {
    return (
      <span ref={ref as React.RefObject<HTMLSpanElement>} className={cls} style={style}>
        {children}
      </span>
    );
  }
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className={cls} style={style}>
      {children}
    </div>
  );
}